import { reactive, ref } from 'vue';
import axios from 'axios';
import { useToast } from './useToast';
import { extractErrorMessage, extractFieldErrors } from '../utils/httpErrors';

const dateInputValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    return '';
};

const nullableNumber = (value) => (value === '' || value == null ? null : Number(value));

const catalogValues = (config, key) => (config?.[key] || []).map((option) => String(option.value));

const blankRequest = () => ({
    name: '',
    client_name: '',
    brand_name: '',
    cities: [],
    age_from: null,
    age_to: null,
    gender: 'all',
    roaming: null,
    incomes: [],
    device_brands: [],
    geo_points: [],
    app_categories: [],
    has_car: null,
    sender_id: '',
    text_ru: '',
    text_kz: '',
    send_date_from: '',
    send_date_to: '',
    send_slots: [],
    sms_count: null,
});

const hydratePoints = (points) => {
    if (!Array.isArray(points)) return [];
    return points
        .filter((point) => point && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng)))
        .map((point) => ({
            lat: Number(point.lat),
            lng: Number(point.lng),
            radius_m: point.radius_m == null || point.radius_m === '' ? null : Number(point.radius_m),
        }));
};

const hydrateRequest = (source, config) => {
    const base = blankRequest();
    if (!source) {
        base.incomes = catalogValues(config, 'incomes');
        base.device_brands = catalogValues(config, 'device_brands');
        base.app_categories = catalogValues(config, 'app_categories');
        return base;
    }

    return {
        ...base,
        ...source,
        name: source.name || '',
        client_name: source.client_name || '',
        brand_name: source.brand_name || '',
        cities: Array.isArray(source.cities) ? source.cities.map(String) : [],
        incomes: Array.isArray(source.incomes) ? source.incomes.map(String) : [],
        device_brands: Array.isArray(source.device_brands) ? source.device_brands.map(String) : [],
        app_categories: Array.isArray(source.app_categories) ? source.app_categories.map(String) : [],
        age_from: source.age_from ?? null,
        age_to: source.age_to ?? null,
        roaming: source.roaming ?? null,
        has_car: source.has_car ?? null,
        gender: source.gender || 'all',
        geo_points: hydratePoints(source.geo_points),
        sender_id: source.sender_id || '',
        text_ru: source.text_ru || '',
        text_kz: source.text_kz || '',
        send_date_from: dateInputValue(source.send_date_from),
        send_date_to: dateInputValue(source.send_date_to),
        send_slots: Array.isArray(source.send_slots) ? source.send_slots.map(String) : [],
        sms_count: source.sms_count ?? null,
    };
};

const scrollToField = (field) => {
    document.querySelector(`[data-field="${field}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const useSmsRequestForm = (props) => {
    const toast = useToast();
    const request = reactive(hydrateRequest(props.smsRequest, props.smsConfig));
    const errors = ref({});
    const generalError = ref('');
    const saving = ref(false);
    const sending = ref(false);
    const sendError = ref('');
    const lastDelivery = ref(props.lastDelivery || null);
    const isEdit = Boolean(props.sendUrl);

    const payload = () => ({
        name: request.name.trim(),
        client_name: request.client_name.trim(),
        brand_name: request.brand_name.trim(),
        cities: [...request.cities],
        age_from: nullableNumber(request.age_from),
        age_to: nullableNumber(request.age_to),
        gender: ['m', 'f', 'all'].includes(request.gender) ? request.gender : '',
        roaming: request.roaming,
        incomes: [...request.incomes],
        device_brands: [...request.device_brands],
        geo_points: (request.geo_points || []).map((point) => ({
            lat: Number(point.lat),
            lng: Number(point.lng),
            radius_m: nullableNumber(point.radius_m),
        })),
        app_categories: [...request.app_categories],
        has_car: request.has_car,
        sender_id: request.sender_id.trim(),
        text_ru: request.text_ru.trim(),
        text_kz: request.text_kz.trim(),
        send_date_from: request.send_date_from || null,
        send_date_to: request.send_date_to || null,
        send_slots: [...(request.send_slots || [])],
        sms_count: nullableNumber(request.sms_count),
    });

    const clientErrors = () => {
        const result = {};
        if (!request.name.trim()) result.name = 'Укажите название заявки';
        else if (request.name.trim().length > 255) result.name = 'Название не длиннее 255 символов';
        if (!request.client_name.trim()) result.client_name = 'Укажите клиента';
        else if (request.client_name.trim().length > 255) result.client_name = 'Имя клиента не длиннее 255 символов';
        if (request.brand_name.trim().length > 255) result.brand_name = 'Название бренда не длиннее 255 символов';
        if (!request.cities.length) result.cities = 'Выберите хотя бы один город';
        if (!['m', 'f', 'all'].includes(request.gender)) result.gender = 'Укажите пол';
        const ageFrom = nullableNumber(request.age_from);
        const ageTo = nullableNumber(request.age_to);
        if ((ageFrom == null) !== (ageTo == null)) {
            result.age_from = 'Укажите возраст от и до, либо оставьте оба поля пустыми';
        } else if (ageFrom != null && ageTo != null) {
            if (ageFrom < 18 || ageFrom > 75 || ageTo < 18 || ageTo > 75) {
                result.age_from = 'Возраст от 18 до 75';
            } else if (ageFrom > ageTo) {
                result.age_to = 'Возраст «до» не может быть меньше возраста «от»';
            }
        }
        if (!/^[A-Za-z]{1,11}$/.test(request.sender_id.trim())) {
            result.sender_id = 'Идентификатор: от 1 до 11 латинских букв';
        }
        if (!request.text_ru.trim()) result.text_ru = 'Укажите текст на русском';
        if (!request.text_kz.trim()) result.text_kz = 'Укажите текст на казахском';
        if (request.text_ru.length > 1000) result.text_ru = 'Текст на русском не длиннее 1000 символов';
        if (request.text_kz.length > 1000) result.text_kz = 'Текст на казахском не длиннее 1000 символов';
        if (!request.send_date_from) result.send_date_from = 'Укажите дату начала отправки';
        if (!request.send_date_to) result.send_date_to = 'Укажите дату окончания отправки';
        if (request.send_date_from && request.send_date_to && request.send_date_to < request.send_date_from) {
            result.send_date_to = 'Дата окончания не может быть раньше даты начала';
        }
        if (!Array.isArray(request.send_slots) || !request.send_slots.length) {
            result.send_slots = 'Выберите хотя бы одно окно отправки';
        }
        const count = nullableNumber(request.sms_count);
        if (count == null || !Number.isInteger(count) || count < 1) {
            result.sms_count = 'Укажите количество SMS';
        }
        const points = Array.isArray(request.geo_points) ? request.geo_points : [];
        if (points.length > 20) {
            result.geo_points = 'Не больше 20 точек';
        } else if (points.some((point) => {
            const lat = Number(point?.lat);
            const lng = Number(point?.lng);
            const radius = Number(point?.radius_m);
            return !Number.isFinite(lat) || lat < -90 || lat > 90
                || !Number.isFinite(lng) || lng < -180 || lng > 180
                || !Number.isInteger(radius) || radius < 100 || radius > 50000;
        })) {
            result.geo_points = 'У каждой точки укажите координаты и радиус от 100 до 50 000 метров';
        }
        return result;
    };

    const applyServerErrors = (error, fallback) => {
        const fieldErrors = extractFieldErrors(error);
        if (Object.keys(fieldErrors).length) {
            errors.value = { ...errors.value, ...fieldErrors };
            scrollToField(Object.keys(fieldErrors)[0]);
            return;
        }
        generalError.value = extractErrorMessage(error, fallback);
    };

    const save = async () => {
        if (!props.writable || saving.value || sending.value) return;
        generalError.value = '';
        errors.value = clientErrors();
        if (Object.keys(errors.value).length) {
            scrollToField(Object.keys(errors.value)[0]);
            return;
        }

        saving.value = true;
        try {
            const { data } = await axios[isEdit ? 'put' : 'post'](props.saveUrl, payload());
            toast.success(data.message || 'Заявка сохранена');
            if (data.redirect) window.location.assign(data.redirect);
        } catch (error) {
            applyServerErrors(error, 'Не удалось сохранить заявку');
        } finally {
            saving.value = false;
        }
    };

    const send = async () => {
        if (!isEdit || !props.writable || sending.value || saving.value) return;
        generalError.value = '';
        sendError.value = '';
        errors.value = clientErrors();
        if (Object.keys(errors.value).length) {
            scrollToField(Object.keys(errors.value)[0]);
            return;
        }

        sending.value = true;
        try {
            const { data } = await axios.post(props.sendUrl, payload());
            lastDelivery.value = data.delivery || null;
            toast.success(data.message || 'Заявка отправлена в Telegram');
        } catch (error) {
            if (error?.response?.data?.delivery) lastDelivery.value = error.response.data.delivery;
            sendError.value = extractErrorMessage(error, 'Не удалось отправить заявку');
            const fieldErrors = extractFieldErrors(error);
            if (Object.keys(fieldErrors).length) {
                errors.value = fieldErrors;
                scrollToField(Object.keys(fieldErrors)[0]);
            }
        } finally {
            sending.value = false;
        }
    };

    return {
        request,
        errors,
        generalError,
        saving,
        sending,
        sendError,
        lastDelivery,
        isEdit,
        save,
        send,
    };
};
